import React, { useEffect, useState } from "react";

import { Public as PublicIcon } from "@mui/icons-material";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  Tooltip,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import moment from "moment";
import { useTranslation } from "react-i18next";

import { ExpireDateDialogState } from "./types";

interface ExpireDateDialogProps {
  expireDateDialog: ExpireDateDialogState;
  onClose: () => void;
  onUpdate: (state: Partial<ExpireDateDialogState>) => void;
  onSubmit: () => void;
}

export const ExpireDateDialog: React.FC<ExpireDateDialogProps> = ({
  expireDateDialog,
  onClose,
  onUpdate,
  onSubmit,
}) => {
  const { t } = useTranslation("bulletin/share");
  const [daysText, setDaysText] = useState<string>("");

  // Calculate the display text for the remaining days
  useEffect(() => {
    if (!expireDateDialog.expireDate) {
      setDaysText("");
      return;
    }

    const today = moment();
    const expire = moment(expireDateDialog.expireDate.toDate());
    const diff = moment.duration(expire.diff(today));
    const days = Math.floor(diff.asDays());

    if (diff.asMilliseconds() > 0) {
      // future date
      if (days === 0) {
        setDaysText(t("expires_today"));
      } else if (days === 1) {
        setDaysText(t("expires_tomorrow"));
      } else {
        setDaysText(t("expires_in_days", { days }));
      }
    } else {
      // expired date
      const absDays = Math.abs(days);
      setDaysText(t("expired", { time: `${absDays} ${t("days")}` }));
    }
  }, [expireDateDialog.expireDate, t]);

  return (
    <Dialog
      open={expireDateDialog.open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <PublicIcon color="primary" />
          <Typography variant="h6">
            {expireDateDialog.level === null
              ? "Update Expiration Date"
              : `${t("public_access")} - ${t("configuration")}`}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        {expireDateDialog.level !== null && (
          <>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                You are granting access to{" "}
                <strong>{expireDateDialog.groupName}</strong>, which is a public
                group. Adjust the configurations before the bulletin is released
                to the public.
              </Typography>
            </Alert>

            <Box sx={{ mb: 3 }}>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: "bold", mb: 1 }}
              >
                Information Redaction
              </Typography>
              <Box component="ul" sx={{ mt: 0, mb: 1, pl: 2 }}>
                <Typography component="li" variant="body2">
                  Narrative removed
                </Typography>
                <Typography component="li" variant="body2">
                  Contact information replaced with:
                  <Box component="ul" sx={{ pl: 2 }}>
                    <Typography component="li" variant="body2">
                      Peterborough Police Non-emergency number: 647-884-2544
                    </Typography>
                    <Typography component="li" variant="body2">
                      Peterborough Crimestoppers number: 647-884-2544
                    </Typography>
                  </Box>
                </Typography>
                <Typography component="li" variant="body2">
                  Addresses replaced with closest street/intersection
                </Typography>
              </Box>
              <Tooltip title="Request changes from support@tryrubicon.com">
                <Button variant="outlined" size="small">
                  Change information redaction
                </Button>
              </Tooltip>
            </Box>
          </>
        )}

        {expireDateDialog.level === null && (
          <Typography variant="body2" sx={{ mb: 3 }}>
            Update the expiration date for{" "}
            <strong>{expireDateDialog.groupName}</strong>.
          </Typography>
        )}

        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
            Configure Expiry Date
          </Typography>
          <DatePicker
            label={t("expire_date")}
            value={expireDateDialog.expireDate}
            onChange={(date) =>
              onUpdate({
                expireDate: date,
              })
            }
            minDate={dayjs()} // not allow to select past date
            format="YYYY-MM-DD"
            slotProps={{
              textField: {
                fullWidth: true,
                variant: "outlined",
                helperText:
                  daysText ||
                  "Access will expire at the end of this date (23:59)",
                color: "secondary",
              },
              actionBar: {
                actions: ["today", "clear"],
              },
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} color="secondary">
          {t("cancel")}
        </Button>
        <Button
          onClick={onSubmit}
          variant="contained"
          color="primary"
          disabled={!expireDateDialog.expireDate}
          startIcon={<PublicIcon />}
        >
          {expireDateDialog.level === null ? "Update" : "Grant Access"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
